import { ArrowUpRight, Github } from "lucide-react";
import { personalInfo, featuredProjects } from "../../data/mock";
import SectionHeading from "../SectionHeading";

const Projects = () => {
  return (
    <section id="projects" className="relative py-24 sm:py-28">
      <div className="rule mb-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          index="03"
          eyebrow="Selected work"
          title="Featured"
          accent="projects"
        >
          A few things I've designed and shipped end to end — from architecture
          down to the last pixel.
        </SectionHeading>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {featuredProjects.map((project) => (
            <a
              key={project.id}
              href={project.live}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex flex-col bg-panel/70 backdrop-blur-sm border border-line rounded-xl overflow-hidden panel-ticks hover:border-signal/40 transition-colors"
            >
              {/* Preview */}
              <div className="relative aspect-[16/10] overflow-hidden border-b border-line bg-ink">
                <img
                  src={project.image}
                  alt={`${project.name} preview`}
                  loading="lazy"
                  className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow mb-1.5">{project.kind}</div>
                    <h3 className="font-display text-2xl font-bold text-fg group-hover:text-signal transition-colors">
                      {project.name}
                    </h3>
                  </div>
                  <ArrowUpRight className="w-5 h-5 shrink-0 text-fg-mute group-hover:text-signal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>

                <p className="mt-3 flex-1 text-fg-dim leading-relaxed">
                  {project.description}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className="font-mono text-[11px] px-2 py-0.5 rounded bg-panel2 text-fg-mute border border-line"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* CTA -> GitHub */}
        <div className="mt-16 flex justify-center">
          <a
            href={personalInfo.github}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 px-6 py-3.5 border border-line text-fg rounded-md font-semibold hover:border-signal/60 hover:text-signal transition-colors"
          >
            <Github className="w-4 h-4" />
            More on GitHub
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects;

/* ------------------------------------------------------------------ *
 * GitHub auto-fetch grid — parked for later.
 *
 * This earlier version pulled repositories live from the GitHub API and
 * rendered them as a filterable grid. Kept here in case we want to bring
 * back an "all repositories" view alongside the featured projects.
 *
 * import { useEffect, useMemo, useState } from "react";
 * import { Star, GitFork, ArrowUpRight, Github } from "lucide-react";
 * import { personalInfo, projects as fallbackProjects } from "../../data/mock";
 * import SectionHeading from "../SectionHeading";
 *
 * const MAX_PROJECTS = 9;
 *
 * const getGithubUsername = () => {
 *   const match = personalInfo.github.match(/github\.com\/([^/]+)/i);
 *   return match ? match[1] : "nfatihakkin";
 * };
 *
 * const getLanguageColor = (language) => {
 *   const colors = {
 *     Python: "#3776ab", Go: "#00add8", Rust: "#dea584", "C++": "#f34b7d",
 *     TypeScript: "#3178c6", JavaScript: "#f1e05a", "C#": "#8bc34a",
 *     HTML: "#e34c26", CSS: "#563d7c"
 *   };
 *   return colors[language] || "#5b6575";
 * };
 *
 * const normalizeFallbackProject = (project) => ({
 *   id: project.id, name: project.name, description: project.description,
 *   language: project.language, stars: project.stars, forks: project.forks,
 *   topics: project.topics || [], homepage: project.homepage || "",
 *   html_url: `https://github.com/${getGithubUsername()}/${project.name}`,
 *   lastUpdated: project.lastUpdated
 * });
 *
 * // Inside the component:
 * //   - fetch `https://api.github.com/users/${username}/repos?per_page=100&sort=updated`
 * //   - filter out forks/archived/private, sort by stars then pushed_at, slice MAX_PROJECTS
 * //   - derive category chips from repo topics
 * //   - render cards showing language dot, stars (>0), forks (>0), topics, links
 * //   - show a skeleton while loading and a "LIVE FROM GITHUB" / "CACHED" badge
 * // (see git history prior to the "featured projects" change for the full body)
 * ------------------------------------------------------------------ */
